import { useNotificationToast } from '@context/NotificationToastContext'
import {
  buyFaxNumber,
  ISinchAvailableNumber,
  searchAvailableFaxNumbers,
  SinchNumberType,
} from '@services/api/fax/numbers.api'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

const toAmount = (price?: { amount?: string }): number => {
  const num = Number(price?.amount)
  return Number.isNaN(num) ? 0 : num
}

type Props = {
  show: boolean
  clinicId: string
  onHide: () => void
  onAdded: () => void
}

interface IPurchaseFaxHooks {
  availableNumbers: ISinchAvailableNumber[]
  busy: boolean
  canProceed: boolean
  displayName: string
  fetchError: string | null
  handleClose: () => void
  handleConfirmProceed: () => Promise<void>
  handleNext: () => void
  handlePrevious: () => void
  isNumbersLoading: boolean
  monthlyAmount: number
  numberType: SinchNumberType | null
  selectedNumber: ISinchAvailableNumber | null
  setDisplayName: Dispatch<SetStateAction<string>>
  setNumberType: Dispatch<SetStateAction<SinchNumberType | null>>
  setSelectedNumber: Dispatch<SetStateAction<ISinchAvailableNumber | null>>
  setupAmount: number
  step: number
  steps: string[]
}

const usePurchaseFax = ({
  show,
  clinicId,
  onHide,
  onAdded,
}: Props): IPurchaseFaxHooks => {
  const { showToast } = useNotificationToast()

  const [step, setStep] = useState(0)
  const [numberType, setNumberType] = useState<SinchNumberType | null>(null)
  const [displayName, setDisplayName] = useState('')
  const [availableNumbers, setAvailableNumbers] = useState<
    ISinchAvailableNumber[]
  >([])
  const [selectedNumber, setSelectedNumber] =
    useState<ISinchAvailableNumber | null>(null)
  const [isNumbersLoading, setIsNumbersLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const steps = ['Select number', 'Review info']

  const reset = (): void => {
    setStep(0)
    setNumberType(null)
    setDisplayName('')
    setAvailableNumbers([])
    setSelectedNumber(null)
    setFetchError(null)
    setBusy(false)
  }

  useEffect(() => {
    if (show) reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show])

  const handleSearch = async (type: SinchNumberType): Promise<void> => {
    setIsNumbersLoading(true)
    setFetchError(null)
    setSelectedNumber(null)
    setAvailableNumbers([])
    try {
      const res = await searchAvailableFaxNumbers({
        regionCode: 'US',
        type,
      })
      if (res.statusCode >= 400) {
        setFetchError(res.message || 'Failed to load available numbers.')
        return
      }
      setAvailableNumbers(res.data?.available_numbers ?? [])
    } catch {
      setFetchError('Failed to load available numbers.')
    } finally {
      setIsNumbersLoading(false)
    }
  }

  // Auto-search whenever the modal opens or the number type changes.
  useEffect(() => {
    if (show && numberType) void handleSearch(numberType)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, numberType])

  const canProceed = useMemo(() => !!selectedNumber, [selectedNumber])

  const monthlyAmount = toAmount(selectedNumber?.monthlyPrice)
  const setupAmount = toAmount(selectedNumber?.setupPrice)

  const handleNext = (): void => {
    if (!canProceed) return
    setStep((prev) => prev + 1)
  }

  const handlePrevious = (): void => setStep((prev) => prev - 1)

  const handleClose = (): void => {
    reset()
    onHide()
  }

  const handleConfirmProceed = async (): Promise<void> => {
    if (!selectedNumber || !clinicId) return
    setBusy(true)
    try {
      const res = await buyFaxNumber({
        clinic_id: clinicId,
        phone_number: selectedNumber.phoneNumber,
        display_name: displayName.trim() || undefined,
      })
      if (res.statusCode !== 200) {
        showToast(
          createFailedToast('Fax numbers', res.message || 'Purchase failed.'),
        )
        return
      }
      showToast(
        createSuccessToast(
          'Purchased',
          `${selectedNumber.phoneNumber} is ready to use.`,
        ),
      )
      onAdded()
      handleClose()
    } catch {
      showToast(createFailedToast('Fax numbers', 'Purchase failed.'))
    } finally {
      setBusy(false)
    }
  }

  return {
    availableNumbers,
    busy,
    canProceed,
    displayName,
    fetchError,
    handleClose,
    handleConfirmProceed,
    handleNext,
    handlePrevious,
    isNumbersLoading,
    monthlyAmount,
    numberType,
    selectedNumber,
    setDisplayName,
    setNumberType,
    setSelectedNumber,
    setupAmount,
    step,
    steps,
  }
}

export default usePurchaseFax
